import { fmtTimeOfDay, fmtDuration, fmtTime } from '../utils/format';
import { useTrackConfig } from '../contexts/track-config-context';
import { Icon, StatTile, cn } from './ui';

// One skater's session on the selected day.
//
// Layout change: the three stats moved from a cramped right-hand cluster onto
// their own row as an even three-column grid, so the times line up down the list
// instead of shifting with the length of each name. The name row now only has to
// hold the name, so it no longer truncates on a 360px screen.
//
// stats = { bestLapMs, avgLapMs, lapCount } as returned by /api/session-stats
export default function SessionCard({ session, stats, lane = 'buitenbaan', onClick, isActive }) {
  const cfg = useTrackConfig();
  const laneDist = cfg.lanes?.find(l => l.id === lane)?.distance ?? 412.4532;

  const bestSpeedKmh = stats?.bestLapMs ? (laneDist / (stats.bestLapMs / 1000)) * 3.6 : null;

  const displayName = session.nickname || session.transponder;
  const hasNickname = !!session.nickname;
  const lapCount = stats?.lapCount ?? 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        'session-card group w-full cursor-pointer rounded-lg border p-3.5 text-left sm:p-4',
        'flex flex-col gap-2.5 transition-[box-shadow,border-color,transform] duration-150 sm:gap-3',
        'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-center lg:gap-5',
        'hover:-translate-y-px hover:border-line-strong hover:shadow-e2',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
        isActive ? 'border-primary-500 bg-primary-100' : 'border-line bg-surface',
      )}
      aria-label={`${displayName} — ${lapCount} ${lapCount === 1 ? 'ronde' : 'ronden'}. Bekijk sessiedetails`}
    >
      {/* Row 1 — identity */}
      <div className="session-identity flex items-center gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-pill font-mono text-[13.5px] font-bold',
            isActive ? 'bg-accent-500 text-white' : 'bg-primary-100 text-primary-700',
          )}
          aria-hidden="true"
        >
          {initialsOf(displayName)}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate font-heading text-[18px] font-bold leading-tight text-ink">
            {displayName}
          </span>
          <span className="mt-0.5 block truncate text-[14px] text-ink-3">
            {/* Transponder-id alleen vanaf tablet: op een telefoon duwde het de
                tijden weg, terwijl de tijd hier het nuttigste is. */}
            {hasNickname && <span className="hidden font-mono sm:inline">{session.transponder} · </span>}
            {fmtTimeOfDay(session.firstPassing)}–{fmtTimeOfDay(session.lastPassing)}
            <span> · {fmtDuration(session.firstPassing, session.lastPassing)}</span>
          </span>
        </span>

        {lapCount > 0 && (
          <span className="shrink-0 whitespace-nowrap rounded-pill border border-line bg-surface-3 px-2.5 py-[3px] font-condensed text-[13.5px] font-bold tracking-[0.04em] text-ink-2">
            {lapCount} {lapCount === 1 ? 'ronde' : 'ronden'}
          </span>
        )}

        <span
          className={cn(
            'flex shrink-0 items-center transition-transform duration-150 group-hover:translate-x-0.5',
            isActive ? 'text-accent-600' : 'text-ink-3',
          )}
          aria-hidden="true"
        >
          <Icon name="chevronRight" size={18} />
        </span>
      </div>

      {/* Row 2 — the three numbers, on a fixed grid so they align down the list */}
      <div className="session-stats grid grid-cols-3 gap-2 border-t border-line pt-3 lg:border-t-0 lg:border-l lg:pl-4 lg:pt-0 [&_dt]:font-body [&_dt]:text-[12px] [&_dt]:normal-case [&_dt]:tracking-normal">
        <StatTile
          variant="bare"
          size="md"
          label="Beste ronde"
          value={stats ? fmtTime(stats.bestLapMs) : '–'}
          tone={stats ? 'green' : 'default'}
        />
        <StatTile variant="bare" size="md" label="Gem. rondetijd" value={fmtTime(stats?.avgLapMs)} sub={stats ? 'zonder pauzeronden' : undefined} />
        <StatTile
          variant="bare"
          size="md"
          label="Beste ronde"
          value={bestSpeedKmh ? bestSpeedKmh.toFixed(1) : '–'}
          unit={bestSpeedKmh ? 'km/h' : undefined}
          sub={bestSpeedKmh ? 'gem. snelheid' : undefined}
          tone={bestSpeedKmh ? 'speed' : 'default'}
        />
      </div>
    </button>
  );
}

function initialsOf(name) {
  const parts = String(name).trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
